import { useCallback } from 'react'
import { useToast } from '@/hooks/use-toast'
import knowledgeApi from '@/lib/knowledge-api'
import { TaxonomyIntent } from '@/hooks/useTaxonomyRun'

interface UseApprovalProps {
  kbId:       string
  intents:    TaxonomyIntent[]
  setIntents: React.Dispatch<React.SetStateAction<TaxonomyIntent[]>>
}

export function useApproval({ kbId, intents, setIntents }: UseApprovalProps) {
  const { toast } = useToast()

  // ── State-machine guard ──────────────────────────────────────────
  // Returns true if the transition is legal. Toasts a warning if not.
  const guardPending = useCallback((currentStatus: string, label: string): boolean => {
    if (currentStatus !== 'pending') {
      toast({
        title: 'Cannot update',
        description: `${label} is already ${currentStatus}. No changes allowed.`,
        variant: 'destructive',
      })
      return false
    }
    return true
  }, [toast])

  // ── Generic optimistic PATCH ─────────────────────────────────────
  // 1. Snapshot current state
  // 2. Apply optimistic update
  // 3. Fire PATCH
  // 4. On error: restore snapshot + toast
  const optimisticPatch = useCallback(async (
    url: string,
    body: Record<string, string>,
    applyUpdate: (prev: TaxonomyIntent[]) => TaxonomyIntent[],
  ) => {
    const snapshot = intents   // closure captures current value
    setIntents(applyUpdate)
    try {
      await knowledgeApi.patch(url, body)
    } catch (err: any) {
      setIntents(snapshot)    // revert
      toast({
        title: 'Update failed',
        description: err?.response?.data?.error || 'Something went wrong. Change reverted.',
        variant: 'destructive',
      })
    }
  }, [intents, setIntents, toast])

  // ── Intent-level actions ──────────────────────────────────────────
  const approveIntent = useCallback((intentId: string) => {
    const intent = intents.find(i => i.id === intentId)
    if (!intent || !guardPending(intent.approved, intent.name)) return
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/intents/${intentId}/`,
      { approved: 'approved' },
      prev => prev.map(i => i.id === intentId ? { ...i, approved: 'approved' } : i),
    )
  }, [intents, kbId, guardPending, optimisticPatch])

  const rejectIntent = useCallback((intentId: string) => {
    const intent = intents.find(i => i.id === intentId)
    if (!intent || !guardPending(intent.approved, intent.name)) return
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/intents/${intentId}/`,
      { approved: 'rejected' },
      prev => prev.map(i => i.id === intentId ? { ...i, approved: 'rejected' } : i),
    )
  }, [intents, kbId, guardPending, optimisticPatch])

  const editIntent = useCallback((intentId: string, name: string, description: string) => {
    // Edit is allowed regardless of approval status (it's metadata, not approval)
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/intents/${intentId}/`,
      { name, description },
      prev => prev.map(i => i.id === intentId ? { ...i, name, description } : i),
    )
  }, [kbId, optimisticPatch])

  // ── Tag-level actions ─────────────────────────────────────────────
  const approveTag = useCallback((tagId: string) => {
    // Find the tag across all intents to get its current status
    const tag = intents.flatMap(i => i.tags).find(t => t.id === tagId)
    if (!tag || !guardPending(tag.approved, tag.name)) return
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/tags/${tagId}/`,
      { approved: 'approved' },
      prev => prev.map(i => ({
        ...i,
        tags: i.tags.map(t => t.id === tagId ? { ...t, approved: 'approved' } : t),
      })),
    )
  }, [intents, kbId, guardPending, optimisticPatch])

  const rejectTag = useCallback((tagId: string) => {
    const tag = intents.flatMap(i => i.tags).find(t => t.id === tagId)
    if (!tag || !guardPending(tag.approved, tag.name)) return
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/tags/${tagId}/`,
      { approved: 'rejected' },
      prev => prev.map(i => ({
        ...i,
        tags: i.tags.map(t => t.id === tagId ? { ...t, approved: 'rejected' } : t),
      })),
    )
  }, [intents, kbId, guardPending, optimisticPatch])

  // ── Entity-type-level actions ─────────────────────────────────────
  const approveEntity = useCallback((etId: string) => {
    const et = intents.flatMap(i => i.entity_types).find(e => e.id === etId)
    if (!et || !guardPending(et.approved, et.name)) return
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/entity-types/${etId}/`,
      { approved: 'approved' },
      prev => prev.map(i => ({
        ...i,
        entity_types: i.entity_types.map(e => e.id === etId ? { ...e, approved: 'approved' } : e),
      })),
    )
  }, [intents, kbId, guardPending, optimisticPatch])

  const rejectEntity = useCallback((etId: string) => {
    const et = intents.flatMap(i => i.entity_types).find(e => e.id === etId)
    if (!et || !guardPending(et.approved, et.name)) return
    optimisticPatch(
      `/knowledge/${kbId}/taxonomy/entity-types/${etId}/`,
      { approved: 'rejected' },
      prev => prev.map(i => ({
        ...i,
        entity_types: i.entity_types.map(e => e.id === etId ? { ...e, approved: 'rejected' } : e),
      })),
    )
  }, [intents, kbId, guardPending, optimisticPatch])

  return {
    approveIntent,
    rejectIntent,
    editIntent,
    approveTag,
    rejectTag,
    approveEntity,
    rejectEntity,
  }
}
